import { type ComponentPropsWithoutRef, type ReactNode, useEffect, useState } from "react";
import { cn } from "../../lib/cn";

export type LiveRegionMode = "polite" | "assertive";

type LiveRegionProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "aria-atomic" | "aria-live" | "children" | "role"
> & {
  atomic?: boolean;
  children?: ReactNode;
  mode?: LiveRegionMode;
  visuallyHidden?: boolean;
};

/**
 * A live region whose wrapper stays mounted while its content changes.
 *
 * Render the component before there is anything to announce, then pass plain
 * content as it changes. Descendants must not carry `role="status"` or
 * `role="alert"`; this wrapper owns the single live role so announcements are
 * not duplicated.
 */
export function LiveRegion({
  atomic = true,
  children,
  className,
  mode = "polite",
  visuallyHidden = false,
  ...props
}: LiveRegionProps) {
  const [announcedContent, setAnnouncedContent] = useState<ReactNode>(null);

  useEffect(() => {
    setAnnouncedContent(children);
  }, [children]);

  return (
    <div
      {...props}
      aria-atomic={atomic}
      aria-live={mode}
      className={cn(visuallyHidden && "sr-only", className)}
      role={mode === "assertive" ? "alert" : "status"}
    >
      {announcedContent}
    </div>
  );
}
